/**
 * <hover-list>
 *
 * Drives sections/section-hover-list.liquid: a list of video titles and a single
 * video frame that swaps between them.
 *
 * Desktop (>= 768px): hovering, focusing or clicking a title shows its video,
 * paused on its poster. Playback only ever starts from a press on the frame.
 *
 * Mobile (< 768px): clicking a title opens it as an accordion. There is only ONE
 * frame in the document, so opening an item MOVES that node into the item's
 * panel. Rendering a second copy would double every video download, which is by
 * far the most expensive thing this section does.
 *
 * NOTHING AUTOPLAYS, and that is load-bearing in three ways:
 *
 *   - Sound works. Browsers only block autoplay that carries audio; because every
 *     play() here originates in a click, the audio comes with it and there is no
 *     mute control to reason about.
 *   - A reduced motion preference is respected by construction. There is no
 *     matchMedia check for it because nothing moves until it is asked to — a
 *     guard would be dead code pretending to do something.
 *   - Combined with preload="none" in the markup, a page carrying this section
 *     downloads poster images only until a visitor presses play.
 *
 * Reparenting a video element pauses it in some browsers, so every move resets to
 * the paused state rather than trusting playback to survive.
 */
if (!customElements.get('hover-list')) {
  customElements.define(
    'hover-list',
    class HoverList extends HTMLElement {
      /* Must stay in step with the design system's md breakpoint — 48rem in
         assets/css-tailwind.css and in this section's own media queries. */
      static DESKTOP_BREAKPOINT = 768;

      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-hover-list-item]'));
        this.panels = Array.from(this.querySelectorAll('[data-hover-list-panel]'));
        this.mobileSlots = Array.from(this.querySelectorAll('[data-hover-list-mobile-slot]'));
        this.videoWraps = Array.from(this.querySelectorAll('[data-hover-list-video]'));
        this.desktopSlot = this.querySelector('[data-hover-list-desktop-slot]');
        this.frame = this.querySelector('[data-hover-list-frame]');
        this.playbackButton = this.querySelector('[data-hover-list-playback]');
        this.iconPlay = this.querySelector('[data-hover-list-icon-play]');
        this.iconPause = this.querySelector('[data-hover-list-icon-pause]');

        if (!this.items.length || !this.frame || !this.desktopSlot) {
          return;
        }

        this.activeIndex = -1;
        this.isOpen = false;

        this.desktop = window.matchMedia(`(min-width: ${HoverList.DESKTOP_BREAKPOINT}px)`);

        this.handleSelect = this.handleSelect.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handlePlaybackClick = this.handlePlaybackClick.bind(this);
        this.handleEnded = this.handleEnded.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        this.items.forEach((item) => {
          /* mouseenter and focus neither bubble, so they are bound per item
             rather than delegated from the root. */
          item.addEventListener('mouseenter', this.handleSelect);
          item.addEventListener('focus', this.handleSelect);
          item.addEventListener('click', this.handleClick);
        });

        this.playbackButton?.addEventListener('click', this.handlePlaybackClick);
        this.desktop.addEventListener('change', this.handleBreakpoint);

        /* Covers a non-looping video reaching its end, so the control does not
           sit showing a pause mark over a stopped video. */
        this.videos().forEach((video) => video.addEventListener('ended', this.handleEnded));

        /* Mobile starts fully collapsed: nothing is open, so not even a poster is
           fetched until the visitor asks for one. */
        if (this.desktop.matches) {
          this.select(0);
        }
      }

      disconnectedCallback() {
        this.items?.forEach((item) => {
          item.removeEventListener('mouseenter', this.handleSelect);
          item.removeEventListener('focus', this.handleSelect);
          item.removeEventListener('click', this.handleClick);
        });

        this.videos().forEach((video) => video.removeEventListener('ended', this.handleEnded));

        this.playbackButton?.removeEventListener('click', this.handlePlaybackClick);
        this.desktop?.removeEventListener('change', this.handleBreakpoint);
      }

      videos() {
        return (this.videoWraps ?? [])
          .map((wrap) => wrap.querySelector('video'))
          .filter(Boolean);
      }

      /** The video currently in the frame, or null. */
      activeVideo() {
        const wrap = this.videoWraps.find((item) => Number(item.dataset.index) === this.activeIndex);

        return wrap?.querySelector('video') ?? null;
      }

      /**
       * Shows one video, rewound and paused on its poster, and marks its title as
       * expanded. Every other video is hidden and stopped.
       */
      select(index) {
        if (index < 0 || index >= this.items.length) {
          return;
        }

        this.activeIndex = index;

        this.items.forEach((item, i) => {
          item.setAttribute('aria-expanded', i === index ? 'true' : 'false');
        });

        this.videoWraps.forEach((wrap) => {
          const isActive = Number(wrap.dataset.index) === index;
          const video = wrap.querySelector('video');

          wrap.hidden = !isActive;

          if (!video) {
            return;
          }

          video.pause();
          video.currentTime = 0;
        });

        this.setPlayingState(false);
      }

      /** Swaps the glyph and the control's accessible name to match playback. */
      setPlayingState(isPlaying) {
        if (this.iconPlay) {
          this.iconPlay.hidden = isPlaying;
        }

        if (this.iconPause) {
          this.iconPause.hidden = !isPlaying;
        }

        if (this.playbackButton) {
          const { labelPlay, labelPause } = this.playbackButton.dataset;
          const next = isPlaying ? labelPause : labelPlay;

          if (next) {
            this.playbackButton.setAttribute('aria-label', next);
          }
        }
      }

      handleSelect(event) {
        if (!this.desktop.matches) {
          return;
        }

        this.select(Number(event.currentTarget.dataset.index));
      }

      handleClick(event) {
        const index = Number(event.currentTarget.dataset.index);

        if (this.desktop.matches) {
          this.select(index);
          return;
        }

        if (this.isOpen && this.activeIndex === index) {
          this.collapse();
        } else {
          this.expand(index);
        }
      }

      handlePlaybackClick() {
        const video = this.activeVideo();

        if (!video) {
          return;
        }

        if (video.paused) {
          this.play(video);
        } else {
          video.pause();
          this.setPlayingState(false);
        }
      }

      handleEnded() {
        this.setPlayingState(false);
      }

      /** Mobile only — moves the single frame into this item's panel. */
      expand(index) {
        const slot = this.mobileSlots[index];

        if (!slot) {
          return;
        }

        this.panels.forEach((panel) => panel.classList.add('hidden'));
        slot.appendChild(this.frame);
        this.panels[index].classList.remove('hidden');
        this.isOpen = true;

        /* After appendChild, not before — reparenting pauses the video. */
        this.select(index);
      }

      /** Closes every panel and parks the frame back in its desktop slot. */
      collapse() {
        this.panels.forEach((panel) => panel.classList.add('hidden'));
        this.desktopSlot.appendChild(this.frame);
        this.items.forEach((item) => item.setAttribute('aria-expanded', 'false'));
        this.videos().forEach((video) => video.pause());
        this.setPlayingState(false);
        this.isOpen = false;
        this.activeIndex = -1;
      }

      handleBreakpoint() {
        this.collapse();

        if (this.desktop.matches) {
          this.select(0);
        }
      }

      /**
       * play() returns a promise that rejects when the browser refuses playback.
       * The control has to stay truthful about what is actually happening, so the
       * glyph only flips once the promise resolves, and flips back if it does not.
       */
      play(video) {
        const attempt = video.play();

        if (!attempt) {
          this.setPlayingState(true);
          return;
        }

        attempt.then(() => this.setPlayingState(true)).catch(() => this.setPlayingState(false));
      }
    },
  );
}
